import React, { useEffect } from "react";
import Navegacao from "../../components/Nav";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import Loading from "../../components/Loading";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";
import { useChamadoForm } from "../../hooks/useChamadoForm";
import TipoSolicitacaoField from "../../components/TipoSolicitacaoField";
import styles from "./Chamado.module.css";

const Chamado = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );

  const navigate = useNavigate();
  useSessionExpirationCheck(sessionExpiration, token);

  const isTecnico = user?.nivel === "1";

  const {
    formData,
    isLoading,
    handleChange,
    handleChangeTipoSolicitacao,
    handleChangeTelefone,
    handleKeyDownTelefone,
    submitChamado,
  } = useChamadoForm({
    token,
    userEmail: user?.email,
    userId: user?.id,
    isTecnico,
  });

  useEffect(() => {
    if (!token) {
      navigate("/area-do-cliente");
      return;
    }
    document.title = isTecnico
      ? "Íkone | Novo Chamado Interno"
      : "Íkone | Abrir Chamado";
  }, [navigate, sessionExpiration, token, isTecnico]);

  return (
    <>
      <Navegacao token={token} user={user} />

      <div className={styles.page}>
        <header className={styles.hero}>
          <div className={styles.heroBlob} aria-hidden="true" />
          <div className={styles.heroContent}>
            <span className={styles.heroBadge}>
              {isTecnico ? "Chamado Interno" : "Central de Suporte"}
            </span>
            <h1 className={styles.heroTitle}>
              {isTecnico
                ? "Abrir Chamado Interno"
                : "Solicitações/Chamados ÍKONE Inspeciona"}
            </h1>
            <p className={styles.heroText}>
              {isTecnico
                ? "Preencha os dados do chamado. O e-mail informado receberá a notificação."
                : "Este formulário deve ser utilizado por cliente ÍKONE, ou por pessoas que as representam, para registrar chamados de falhas ou melhorias nos sistemas."}
            </p>
          </div>
        </header>

        <main className={styles.formWrapper}>
          <form onSubmit={submitChamado} className={styles.formCard}>
            <div className={styles.formHeader}>
              <h2 className={styles.formTitle}>Dados do chamado</h2>
              <p className={styles.formSubtitle}>
                Preencha os campos abaixo com atenção.{" "}
                <span className={styles.required}>*</span> obrigatórios.
              </p>
            </div>

            <div className={styles.grid}>
              <div className={styles.field}>
                <label htmlFor="empresa" className={styles.label}>
                  Nome da Empresa <span className={styles.required}>*</span>
                </label>
                <input
                  id="empresa"
                  className={styles.input}
                  type="text"
                  placeholder="Digite o nome da empresa"
                  value={formData.empresa}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className={styles.field}>
                <div>
                  <label htmlFor="num_chamado" className={styles.label}>
                    Número Chamado Interno
                  </label>
                  <span className={styles.optional}>(OPCIONAL)</span>
                </div>
                <input
                  id="num_chamado"
                  className={styles.input}
                  type="text"
                  placeholder="Digite o número do chamado"
                  value={formData.num_chamado}
                  onChange={handleChange}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="email" className={styles.label}>
                  Email para contato <span className={styles.required}>*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  className={styles.input}
                  placeholder="Email"
                  disabled={!isTecnico} // técnico pode alterar; cliente fica fixo
                  value={isTecnico ? formData.email : (user?.email ?? "")}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="telefone" className={styles.label}>
                  Telefone <span className={styles.required}>*</span>
                </label>
                <input
                  type="tel"
                  id="telefone"
                  className={styles.input}
                  placeholder="(00) 00000-0000"
                  value={formData.telefone}
                  onChange={handleChangeTelefone}
                  onKeyDown={handleKeyDownTelefone}
                  required
                />
              </div>

              <TipoSolicitacaoField
                value={formData.tipo_solicitacao}
                onChange={handleChangeTipoSolicitacao}
              />

              <div className={`${styles.field} ${styles.fullWidth}`}>
                <label htmlFor="descricao" className={styles.label}>
                  Descrição <span className={styles.required}>*</span>
                </label>
                <textarea
                  id="descricao"
                  className={styles.textarea}
                  rows={4}
                  placeholder="Descreva o chamado/solicitação de forma detalhada"
                  value={formData.descricao}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <button
              disabled={isLoading}
              type="submit"
              className={styles.submitButton}
            >
              {isLoading ? (
                <Loading isButton corPredefinida color="white" />
              ) : isTecnico ? (
                "Criar Chamado Interno"
              ) : (
                "Enviar Solicitação"
              )}
            </button>
          </form>
        </main>
      </div>
    </>
  );
};

export default Chamado;
